import React, { useState } from "react";
import { AlertTriangle, ShieldCheck, Mail, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: "malware", label: "Malware / malicious redirect" },
  { value: "phishing", label: "Phishing / credential theft" },
  { value: "adult", label: "Adult content on non-adult inventory" },
  { value: "ip-infringement", label: "Copyright / IP infringement (DMCA)" },
  { value: "trademark", label: "Trademark misuse" },
  { value: "misleading", label: "Misleading or deceptive creative" },
  { value: "gambling", label: "Prohibited gambling / regulated content" },
  { value: "other", label: "Other" },
];

export default function Abuse() {
  const [form, setForm] = useState({
    reporter_name: "",
    reporter_email: "",
    company: "",
    category: "malware",
    reported_url: "",
    ad_creative_url: "",
    description: "",
    evidence_url: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.description.trim().length < 10) {
      setError("Please provide at least 10 characters describing the issue.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/abuse`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (r.ok) setDone(true);
      else {
        const d = await r.json().catch(() => ({}));
        setError(d.detail || "Submission failed. Please try again.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main className="relative min-h-screen grid place-items-center px-6 pt-32 pb-20">
        <div className="aurora-orb bg-[#0047FF] w-[500px] h-[500px] top-0 left-1/2 -translate-x-1/2 opacity-25" />
        <div
          data-testid="abuse-success"
          className="glass-strong rounded-3xl p-10 md:p-14 max-w-xl text-center relative z-10"
        >
          <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-[#0047FF] to-[#00F0FF] grid place-items-center mb-6">
            <ShieldCheck className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mb-3">
            Report received
          </h1>
          <p className="text-white/65 mb-6">
            Thank you. Our Trust &amp; Safety team will review your report and
            respond to <span className="text-white">{form.reporter_email}</span>{" "}
            within 48 business hours. Confirmed policy violations are actioned
            immediately.
          </p>
          <a href="/" className="btn-primary">
            Back to Home <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </main>
    );
  }

  return (
    <main className="relative min-h-screen pt-32 pb-20">
      <div className="aurora-orb bg-[#0047FF] w-[600px] h-[600px] -top-40 -left-40 opacity-25" />
      <div className="aurora-orb bg-[#8A2BE2] w-[500px] h-[500px] top-1/2 right-0 opacity-20" />

      <div className="container-x relative z-10 grid lg:grid-cols-[1fr_1.3fr] gap-12 items-start">
        {/* Left — pitch */}
        <div>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-6">
            <AlertTriangle className="w-3.5 h-3.5 text-[#00F0FF]" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-white/80">
              Trust &amp; Safety
            </span>
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] mb-6">
            Report <span className="aurora-text">abuse</span>
          </h1>
          <p className="text-white/65 leading-relaxed mb-8 max-w-md">
            MonetiseUP takes ad quality, user safety, and intellectual-property
            rights seriously. Use this form to report any advertisement, landing
            page, or publisher inventory in our network that you believe
            violates our policies or your rights.
          </p>

          <div className="space-y-4 max-w-md">
            <div className="tile p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] mb-2">
                Response window
              </div>
              <div className="text-sm text-white/80">
                We acknowledge every report within one business day and complete
                initial review inside 48 business hours.
              </div>
            </div>
            <div className="tile p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] mb-2">
                DMCA / IP notices
              </div>
              <div className="text-sm text-white/80">
                For copyright or trademark claims, please include the URL of
                the original work, your ownership evidence, and a good-faith
                statement. Formal DMCA notices can also be sent to{" "}
                <span className="text-white">business@responsiq.com</span>.
              </div>
            </div>
            <div className="tile p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] mb-2">
                False reports
              </div>
              <div className="text-sm text-white/80">
                Deliberately false or malicious reports may be forwarded to law
                enforcement. Please only submit reports you believe to be
                accurate.
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-8 text-sm text-white/60">
            <Mail className="w-4 h-4" />
            Direct email:&nbsp;
            <a
              href="mailto:business@responsiq.com"
              className="text-[#00F0FF] hover:text-white transition-colors"
            >
              business@responsiq.com
            </a>
          </div>
        </div>

        {/* Right — form */}
        <form
          onSubmit={submit}
          data-testid="abuse-form"
          className="glass-strong rounded-3xl p-6 md:p-8 space-y-4"
        >
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Your name *">
              <input
                required
                value={form.reporter_name}
                onChange={set("reporter_name")}
                data-testid="abuse-name"
                className="input"
              />
            </Field>
            <Field label="Your email *">
              <input
                required
                type="email"
                value={form.reporter_email}
                onChange={set("reporter_email")}
                data-testid="abuse-email"
                className="input"
              />
            </Field>
          </div>

          <Field label="Company (optional)">
            <input
              value={form.company}
              onChange={set("company")}
              className="input"
            />
          </Field>

          <Field label="Category *">
            <select
              value={form.category}
              onChange={set("category")}
              data-testid="abuse-category"
              className="input"
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="URL or domain being reported *">
            <input
              required
              value={form.reported_url}
              onChange={set("reported_url")}
              placeholder="https://example.com/page-with-issue"
              data-testid="abuse-url"
              className="input"
            />
          </Field>

          <Field label="Ad creative URL (if applicable)">
            <input
              value={form.ad_creative_url}
              onChange={set("ad_creative_url")}
              placeholder="https://cdn.example.com/banner.jpg"
              className="input"
            />
          </Field>

          <Field label="Description *">
            <textarea
              required
              rows={5}
              value={form.description}
              onChange={set("description")}
              placeholder="Describe what you observed, when, and any additional context that helps our team investigate."
              data-testid="abuse-description"
              className="input resize-y"
            />
          </Field>

          <Field label="Evidence link (screenshot / recording, optional)">
            <input
              value={form.evidence_url}
              onChange={set("evidence_url")}
              placeholder="https://drive.google.com/... or Loom / imgur link"
              className="input"
            />
          </Field>

          {error && (
            <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="abuse-submit"
            className="btn-primary w-full justify-center disabled:opacity-50"
          >
            {submitting ? "Submitting..." : "Submit report"}
            {!submitting && <ArrowRight className="w-4 h-4" />}
          </button>
          <p className="text-[11px] text-white/40 text-center">
            By submitting, you confirm the details provided are accurate to the
            best of your knowledge.
          </p>
        </form>
      </div>

      <style>{`
        .input {
          width: 100%;
          padding: 0.75rem 1rem;
          border-radius: 0.75rem;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          color: #fff;
          font-size: 14px;
          transition: border-color .2s;
        }
        .input:focus { outline: none; border-color: rgba(0,240,255,0.5); }
        .input::placeholder { color: rgba(255,255,255,0.3); }
      `}</style>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
